import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { GET_CLIMBERS_PROGRESS } from "../api/queries";
import { graphQLClient } from "../graphqlClient";
import { useReactQueryErrorReporter } from "./use-error-reporter";
import { useProductFeatures } from "./useProductFeatures";
import type { ClimberKeyInput, ClimberProgress } from "../types/mmp-graphql";

interface GetClimbersProgressResponse {
  getClimbersProgress: (ClimberProgress | null)[];
}

// getClimbersProgress is backed by DynamoDB BatchGetItem (max 100 keys).
const CHUNK_SIZE = 100;

/**
 * Batch-loads progress records (prg-<productCode>) for a list of climbers —
 * used by teacher roster and climber-detail views. Each climber's districtId
 * is their record's parentID (climbers are parented to their district).
 *
 * Returns a map of climberId → ClimberProgress. Climbers with no progress
 * record yet (lazy creation) are absent from the map.
 */
export const useClimbersProgress = (climbers: ClimberKeyInput[]) => {
  const { activeProduct, isLoading: productLoading } = useProductFeatures();
  const productCode = activeProduct?.code;

  // Stable key so reordered rosters don't refetch.
  const climberIdsKey = climbers
    .map((c) => c.climberId)
    .sort()
    .join(",");

  // NOTE: the query caches a plain ARRAY (not a Map). React Query persistence
  // (persistQueryClient -> localStorage) JSON-serializes the cache, and a Map
  // serializes to "{}" and rehydrates as a non-Map plain object (breaking
  // `.get`). The Map is rebuilt client-side from the serializable array below.
  const { data, error, isLoading, refetch } = useQuery<ClimberProgress[]>({
    queryKey: ["climbersProgress", productCode, climberIdsKey],
    queryFn: async () => {
      const records: ClimberProgress[] = [];

      for (let i = 0; i < climbers.length; i += CHUNK_SIZE) {
        const chunk = climbers.slice(i, i + CHUNK_SIZE);
        const resp = await graphQLClient.request<GetClimbersProgressResponse>(
          GET_CLIMBERS_PROGRESS,
          { climbers: chunk, productCode },
        );
        // BatchGetItem doesn't guarantee order and omits records that don't
        // exist; keep the records that resolved.
        (resp.getClimbersProgress ?? []).forEach((progress) => {
          if (progress?.parentID) records.push(progress);
        });
      }

      return records;
    },
    enabled: !!productCode && climbers.length > 0,
  });

  useReactQueryErrorReporter("Could not load climber progress", error, false);

  // Key by the record's own parentID (the climberId). Guard with Array.isArray:
  // a legacy persisted cache entry (from when this query cached a Map) rehydrates
  // as a non-array "{}" object, which would otherwise throw here until refetch.
  const progressByClimberId = useMemo(() => {
    const map = new Map<string, ClimberProgress>();
    (Array.isArray(data) ? data : []).forEach((progress) => {
      if (progress?.parentID) map.set(progress.parentID, progress);
    });
    return map;
  }, [data]);

  return {
    progressByClimberId,
    productCode,
    loading: isLoading || productLoading,
    error,
    refetch,
  };
};
